import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useEffect, useState } from 'react';
import { Tooltip } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { cloneDeep } from 'lodash';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { TextBlock } from '../../Atoms/Text';
import { Asset, isShowDecommissionModalToParentAsset } from '../../../store/Asset';
import { getAsset, patchAsset } from '../../../Api/Asset/AssetApi';
import { Icon } from '../../Atoms/Icon';
import { Text } from '../../Atoms/Text';
import { getUTCRunDate } from '../../../utils/helpers';
import { ComponentSpinner } from '../../Compounds/Loading/ComponentSpinner';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { CustomActionBar } from '../../Compounds/DetailPanel/CustomActionBar';
import { RecordLink } from '../../Atoms/RecordLink';

export interface DialogParams {
  asset: Asset;
}

export const DecommissionParentDialog = ({ asset }: DialogParams) => {
  const [parentAsset, setParentAsset] = useState<Asset | null>(null);
  const [open, setOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [tempDate, setTempDate] = useState<Date | null>(null);
  const { setSnack } = useSnackbar();

  const getAssetParent = async (id: string) => {
    setParentAsset(null);
    const { data } = await getAsset(id);
    setParentAsset(data as Asset | null);
  };

  useEffect(() => {
    if (!asset || !asset.replacementOfAssetID) return;
    getAssetParent(asset.replacementOfAssetID);
  }, [asset.replacementOfAssetID, asset.commissionsStatus, asset.actualDecommissionedDate]);

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmitForm = async () => {
    if (!parentAsset || !tempDate) return;

    const clonedParentAsset = cloneDeep(parentAsset);
    clonedParentAsset.actualDecommissionedDate = getUTCRunDate(tempDate).toISOString();
    clonedParentAsset.commissionsStatus = 'Decommissioned';
    setIsSubmitting(true);
    const { error } = await patchAsset(clonedParentAsset);
    if (error) {
      setSnack({ message: error.message, type: 'error', open: true });
    }
    getAssetParent(clonedParentAsset.id);
    setIsSubmitting(false);
  };

  const isShowModal = isShowDecommissionModalToParentAsset(asset, parentAsset);

  useEffect(() => {
    if (!isSubmitting) setOpen(isShowModal);
  }, [isShowModal, isSubmitting]);

  useEffect(() => {
    if (asset.actualFirstCommissionRun) {
      const date = getUTCRunDate(asset.actualFirstCommissionRun);
      date.setMonth(date.getMonth() - 1);
      setTempDate(date);
    } else {
      setTempDate(null);
    }
  }, [asset.actualFirstCommissionRun]);

  const salesforceAssetUrl = `${process.env.REACT_APP_SALESFORCE_INSTANCE_URL}/lightning/r/Asset`;

  return (
    <Dialog
      data-testid="decommission-parent-dialog"
      maxWidth="md"
      open={open}
      onClose={handleClose}>
      <DialogTitle>
        <TextBlock
          weight="bold"
          size="lg"
          color="grey9">
          Decommission Parent Asset
        </TextBlock>
      </DialogTitle>
      <DialogContent
        sx={{
          minHeight: 70,
          minWidth: 420,
          display: 'flex flex-col',
          alignItems: 'center',
          justifyContent: 'left',
          overflowY: 'visible'
        }}>
        <Text
          dataTestId="replacement-of-label-field"
          size="sm14"
          color="grey6">
          Replacement Of
        </Text>
        <div className="flex justify-start">
          <RecordLink
            data-testid="replacement-of-asset-link"
            to={`/asset/${asset.replacementOfAssetID}`}>
            <div className="flex items-center text-small">{asset.replacementOfAssetNumber}</div>
          </RecordLink>
          <Tooltip
            arrow
            title="Open in SF"
            placement="bottom">
            <a
              data-testid="openInSalesforceButton"
              href={`${salesforceAssetUrl}/${asset.replacementOfAssetSFID}/view `}
              target="_blank"
              rel="noreferrer">
              <Icon type="external" />
            </a>
          </Tooltip>
        </div>
        <Text
          dataTestId="commissions-status-label-field"
          className="mt-2"
          size="sm14"
          color="grey6">
          Commissions Status To
        </Text>
        <Text
          dataTestId="commissions-status-value"
          size="sm14">
          Decommissioned
        </Text>
        <Text
          dataTestId="actual-decommission-run-label-field"
          className="mt-2"
          size="sm14"
          color="grey6">
          Actual Decommission Run To
        </Text>
        <DatePicker
          closeOnSelect={false}
          disabled={isSubmitting}
          data-testid="decommission-date-picker"
          className="w-40"
          slots={{
            actionBar: CustomActionBar
          }}
          slotProps={{
            actionBar: {
              actions: ['next', 'accept', 'cancel']
            },
            textField: {
              size: 'small'
            }
          }}
          views={['month', 'year']}
          value={tempDate}
          onChange={(v) => setTempDate(v)}
          format={'MMM yyyy'}
        />
      </DialogContent>
      <hr className="mb-1 mx-6" />
      <DialogActions sx={{ marginRight: 2, pb: 2 }}>
        <button
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          onClick={handleClose}>
          Cancel
        </button>
        {isSubmitting ? (
          <div className="flex">
            <ComponentSpinner
              sx={{ mt: '0', m: '0' }}
              label="Saving..."
            />
          </div>
        ) : (
          <button
            data-testid="submit-confirmation"
            onClick={handleSubmitForm}
            disabled={!tempDate}
            className={DEFAULT_ADVANCE_BUTTON_STYLE}
            type="submit">
            Save
          </button>
        )}
      </DialogActions>
    </Dialog>
  );
};
